import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useScrollToTop } from './hooks/useScrollToTop';

// Layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import DashboardLayout from './components/layout/DashboardLayout';

// Public pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import DocumentationPage from './pages/DocumentationPage';
import ServicesPage from './pages/ServicesPage';
import BlogPage from './pages/BlogPage';

// Legal pages
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';

// Additional pages
import CareersPage from './pages/CareersPage';
import StatusPage from './pages/StatusPage';

// Auth pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationCallback from './pages/EmailVerificationCallback';
import AuthCallback from './pages/AuthCallback';

// Dashboard pages
import DashboardPage from './pages/DashboardPage';
import TrendsPage from './pages/TrendsPage';
import AlertsPage from './pages/AlertsPage';
import ReportsPage from './pages/ReportsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import UsagePage from './pages/UsagePage';
import ApiKeysPage from './pages/ApiKeysPage';

import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import AudienceAnalysisPage from './pages/AudienceAnalysisPage';
import ForecastingPage from './pages/ForecastingPage';
import BusinessIntelligencePage from './pages/BusinessIntelligencePage';
import CustomReportsPage from './pages/CustomReportsPage';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';
import ProtectedRoute from './components/auth/ProtectedRoute';


// Home route component that shows different layouts based on auth state
const HomeRoute: React.FC = () => {
  // Check if this is a Supabase auth callback
  if (window.location.hash.includes('access_token')) {
    return <AuthCallback />;
  }
  
  // Always show header + content + footer for homepage
  // Header will show different nav items based on auth state
  return (
    <>
      <Header />
      <HomePage />
      <Footer />
    </>
  );
};

// Docs route component that shows different layouts based on auth state
const DocsRoute: React.FC = () => {
  // Always show header + content + footer for docs page
  // Header will show different nav items based on auth state
  return (
    <>
      <Header />
      <DocumentationPage />
      <Footer />
    </>
  );
};

// Scroll restoration component
const ScrollRestoration: React.FC = () => {
  useScrollToTop();
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppProvider>
          <Router>
          <ScrollRestoration />
          <div className="min-h-screen bg-gray-50 pt-16">
            <Toaster position="top-right" />
            
            {/* Public routes */}
            <Routes>
              <Route path="/" element={
                <HomeRoute />
              } />
              
              
              <Route path="/about" element={
                <>
                  <Header />
                  <AboutPage />
                  <Footer />
                </>
              } />
              
              <Route path="/pricing" element={
                <>
                  <Header />
                  <PricingPage />
                  <Footer />
                </>
              } />
              
              <Route path="/contact" element={
                <>
                  <Header />
                  <ContactPage />
                  <Footer />
                </>
              } />
              
              <Route path="/docs" element={
                <DocsRoute />
              } />
              
              <Route path="/services" element={
                <>
                  <Header />
                  <ServicesPage />
                  <Footer />
                </>
              } />
              
              <Route path="/blog" element={
                <>
                  <Header />
                  <BlogPage />
                  <Footer />
                </>
              } />
              
              {/* Legal pages */}
              <Route path="/terms" element={
                <>
                  <Header />
                  <TermsOfServicePage />
                  <Footer />
                </>
              } />
              
              <Route path="/privacy" element={
                <>
                  <Header />
                  <PrivacyPolicyPage />
                  <Footer />
                </>
              } />
              
              <Route path="/cookies" element={
                <>
                  <Header />
                  <CookiePolicyPage />
                  <Footer />
                </>
              } />
              
              <Route path="/careers" element={
                <>
                  <Header />
                  <CareersPage />
                  <Footer />
                </>
              } />
              
              <Route path="/status" element={
                <>
                  <Header />
                  <StatusPage />
                  <Footer />
                </>
              } />
              
              {/* Auth routes */}
              <Route path="/login" element={
                <>
                  <Header />
                  <LoginPage />
                  <Footer />
                </>
              } />
              
              <Route path="/signup" element={
                <>
                  <Header />
                  <SignupPage />
                  <Footer />
                </>
              } />

              
              <Route path="/forgot-password" element={
                <>
                  <Header />
                  <ForgotPasswordPage />
                  <Footer />
                </>
              } />
              
              <Route path="/reset-password" element={
                <>
                  <Header />
                  <ResetPasswordPage />
                  <Footer />
                </>
              } />

              <Route path="/auth/verify-email" element={
                <EmailVerificationCallback />
              } />

              <Route path="/auth/callback" element={
                <AuthCallback />
              } />
              
              {/* Dashboard routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/trends" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <TrendsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/alerts" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AlertsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/reports" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ReportsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AnalyticsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/usage" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <UsagePage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/api-keys" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ApiKeysPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              

              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/billing" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <BillingPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/audience-analysis" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AudienceAnalysisPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/forecasting" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ForecastingPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/business-intelligence" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <BusinessIntelligencePage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              <Route path="/custom-reports" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CustomReportsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              } />
            </Routes>
          </div>
        </Router>
        </AppProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App; 